/**
 * useQuickFill - Hook for Quick-Fill Actions
 *
 * Extracts quick-fill logic from ProjectSetupPage to improve separation of concerns
 * and make quick-fill actions reusable/testable.
 *
 * Usage:
 * ```tsx
 * const quickFill = useQuickFill(projectId);
 * <Button onClick={quickFill.dates}>Quick Fill Dates</Button>
 * ```
 */

import { useCallback } from 'react';

import { useProjectSetupHandlers } from '~/hooks/useProjectSetupHandlers';
import { LAKEVIEW_QUICK_FILL_DATA } from '~/services/dev-utilities/lakeview-data';
import type { DateInputsForm, DRFInputsForm, MillageInputsForm, TaxValueInputsForm } from '~/types';

export const useQuickFill = (projectId: string) => {
  const handlers = useProjectSetupHandlers(projectId);

  const quickFillDates = useCallback(() => {
    const lakeviewDates: DateInputsForm = {
      startingTaxYear: LAKEVIEW_QUICK_FILL_DATA.dates.startingTaxYear,
      fiscalYearEnd: LAKEVIEW_QUICK_FILL_DATA.dates.fiscalYearEnd,
      taxCollectionSchedule: LAKEVIEW_QUICK_FILL_DATA.dates.taxCollectionSchedule,
      paymentDates: LAKEVIEW_QUICK_FILL_DATA.dates.paymentDates.map((pd) => ({ ...pd })),
    };
    handlers.handleDateInputsChange(lakeviewDates);
  }, [handlers]);

  const quickFillTaxValues = useCallback(() => {
    const lakeviewTax: TaxValueInputsForm = {
      initialTaxableValue: LAKEVIEW_QUICK_FILL_DATA.tax.initialTaxableValue,
      startYearGrowthRate: LAKEVIEW_QUICK_FILL_DATA.tax.startYearGrowthRate,
      fiveYearGrowthRate: LAKEVIEW_QUICK_FILL_DATA.tax.fiveYearGrowthRate,
      twentyYearGrowthRate: LAKEVIEW_QUICK_FILL_DATA.tax.twentyYearGrowthRate,
      startingTaxYear: LAKEVIEW_QUICK_FILL_DATA.tax.startingTaxYear,
    };
    handlers.handleTaxValueInputsChange(lakeviewTax);
  }, [handlers]);

  const quickFillMillage = useCallback(() => {
    const lakeviewMillage: MillageInputsForm = {
      peakMillage: LAKEVIEW_QUICK_FILL_DATA.millage.peakMillage,
      peakMillageDuration: LAKEVIEW_QUICK_FILL_DATA.millage.peakMillageDuration,
      minimumMillage: LAKEVIEW_QUICK_FILL_DATA.millage.minimumMillage,
      minimumMillageDuration: LAKEVIEW_QUICK_FILL_DATA.millage.minimumMillageDuration,
    };
    handlers.handleMillageInputsChange(lakeviewMillage);
  }, [handlers]);

  const quickFillDRF = useCallback(() => {
    const lakeviewDRF: DRFInputsForm = {
      startingBalance: LAKEVIEW_QUICK_FILL_DATA.drf.startingBalance,
      minimumBalance: LAKEVIEW_QUICK_FILL_DATA.drf.minimumBalance,
      maximumBalance: LAKEVIEW_QUICK_FILL_DATA.drf.maximumBalance,
    };
    handlers.handleDRFInputsChange(lakeviewDRF);
  }, [handlers]);

  return {
    quickFillDates,
    quickFillTaxValues,
    quickFillMillage,
    quickFillDRF,
  };
};
